import React, { useEffect, useState } from 'react';
import 'styles/home.css';
import 'styles/home-page.css';

import { locationForRoute } from 'files/routes.js';
import Request from 'files/Request.js';
import { campaign, trackEvent } from 'files/analytics.js';
import { SiteFooter, SiteNav, scrollToSection } from 'views/SiteChrome.js';
import BotTrap, { trapValue } from 'views/BotTrap.js';
import { findArticle, useArticles } from 'files/articles.js';
import { contourField, growthRings, scopeForms } from 'files/rings.js';
import { isMobile as phoneWidth } from 'files/plain.js';
import { scoreAnswers } from 'files/quick-look-model.js';

/* Built on the method in design/deep-look-page.md. How this page applies it,
 * and the decisions behind the fold and the mid-page Deep Look section, are in
 * design/home-page.md.
 *
 * The hero plays the Quick Look rather than describing it.
 *
 * One owner's answers, run through the real model. The questions, their hints,
 * their options and the read all come from quick-look-model.js, so nothing here
 * is copy written to look like the tool, and the hero follows the model if the
 * model changes. The only thing authored here is which answers this owner gave.
 *
 * A service business that wants its time back, working around its tools, with
 * information moving by hand. Ordinary, and it produces three real openings. */
// generated once at module load, not per render — the shape must never change
const RINGS = growthRings();

// the one ring picked out in accent, so the eye has somewhere to land
const ACCENT_RING = 5;

/* Contours for the margins of the Quick Look band — a different motif from the
   hero on purpose. Two seeds rather than one drawing mirrored, so the sides are
   not a reflection of each other. */
/* The fold's own margins. Same generator and the same hand as the Quick Look
   band at the foot of the page, deliberately not the same drawing: other seeds
   and other line counts, so the two CTA moments read as bookends rather than as
   one image used twice. */
const FOLD_HEIGHT = 900;
const FOLD_LEFT = contourField({ from: 'left', lines: 7, seed: 7, height: FOLD_HEIGHT });
const FOLD_RIGHT = contourField({ from: 'right', lines: 6, seed: 11, height: FOLD_HEIGHT });

const EDGE_LEFT = contourField({ from: 'left', lines: 9, seed: 0 });
const EDGE_RIGHT = contourField({ from: 'right', lines: 8, seed: 3 });

const DEMO_ANSWERS = {
    business: 'service',
    headcount: 'to_50',
    horizon: 'free_up_time',
    ambition: 'pushed_aside',
    ceiling: 'work_around',
    movement: 'by_hand',
    reporting: 'by_hand'
};

/* All seven, in the order the tool asks them. Three of them, starting at
   question four, meant the counter opened on "4 of 7" with nothing before it,
   which reads as having missed the beginning rather than as a summary. */
const DEMO_READ = scoreAnswers(DEMO_ANSWERS);

const HomePage = ({ active, utils }) => {

    /* Only the read-next links under the problems hang off this, so the page is
       whole without it and simply gains a few links a moment later. Nothing here
       waits on the request. */
    const library = useArticles();
    const isMobile = phoneWidth();

    // the closing enquiry
    const [enquiry, setEnquiry] = useState({ email: '', name: '', problem: '' });
    const [failure, setFailure] = useState(null);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    /* Two ways to arrive at a section rather than the top of the page: a fragment
       in the URL, which the browser resolves before React has rendered anything
       and therefore gives up on, and a nav click from another page, which routes
       here carrying active.section. Both are honoured once the sections exist. */
    useEffect(() => {

        const id = (active && active.section) || window.location.hash.replace('#', '');
        if(!id) {
            return;
        }
        const frame = window.requestAnimationFrame(() => scrollToSection(id));
        return () => window.cancelAnimationFrame(frame);

    }, [active]);

    // The legacy app paints the body from its theme classes; the marketing page
    // owns its own ground, so take it over while this view is mounted.
    useEffect(() => {
        document.body.classList.add('dl-body');
        return () => {
            document.body.classList.remove('dl-body');
        }
    }, []);

    /* The hero demo's playback.
     *
     * A title card, seven questions, then the read. It plays once and holds the
     * last frame, because that frame has the offer in it and a hero that moves
     * forever is a hero nobody reads past.
     *
     * Playback waits for the panel to be seen. Until then it rests on the title
     * card, which is a sensible thing to be looking at rather than a blank.
     */
    const onContactClick = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'contact' });
    }

    const onQuickLookClick = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'quick-look' });
    }

    const onDeepLookClick = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'deep-look' });
    }

    const onReportClick = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'deep-look-report' });
    }

    const onProcessClick = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'how-we-work' });
    }

    const onEnquiryChange = key => evt => {
        setEnquiry(enquiry => ({ ...enquiry, [key]: evt.target.value }));
    }

    /* The legacy contact form splits a name in two and asks for a project brief.
       This asks for three things and fills in the rest, so the submission still
       arrives in the shape the server already understands. */
    const onEnquirySubmit = async evt => {

        evt.preventDefault();
        if(sending) {
            return;
        }

        const name = enquiry.name.trim();
        const trap = trapValue(evt);

        setFailure(null);
        setSending(true);
        try {
            /* new_contact_form, not new_website_contact_form. The old site's
               endpoint requires a phone number, a service list and a start date
               before it will accept anything, none of which this form collects,
               so every submission from here was coming back a 400. */
            await Request.post(utils, '/utils/', {
                email_address: enquiry.email.trim(),
                message: enquiry.problem.trim(),
                name: name,
                company_website: trap,
                source: 'home',
                type: 'new_contact_form',

                // stored with the enquiry, not just counted in analytics — see
                // the note on the same field in QuickLook
                campaign: campaign()
            });
            setSending(false);
            setSent(true);
            trackEvent('contact-submitted', { from: 'home' });

        } catch(e) {
            setSending(false);
            setFailure(`We couldn’t send that. ${e.message || 'Please try again'}, or email hello@duralabs.io directly.`);
        }
    }

    const onArticleClick = slug => evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'article', slug: slug });
    }

    /* A link out to writing on the same problem. Returns nothing when the slug
       is missing or the piece is not published, so a section can carry links on
       some entries and not others without ever pointing at a page that does not
       exist. Publish the article on the server first, then name the slug here. */
    const getArticleLink = slug => {

        const article = slug ? findArticle(library.articles, slug) : null;
        if(!article) {
            return null;
        }
        return (
            <a
            className={'dl-read'}
            href={`/articles/${article.slug}`}
            onClick={onArticleClick(article.slug)}>
                {article.short || article.title} <span className={'dl-arrow'}>→</span>
            </a>
        )
    }

    /* Growth rings. It says what the page argues — something small that grew
       outward over years and is still standing — and it says it as a drawing
       rather than a diagram.

       Rules if you replace it: nothing in here may be regular. The version this
       replaced was five strands resolving into evenly spaced parallel lines, and
       even spacing plus straight lines reads as machinery, not as a mark. Line
       only, no fill, no labels. One ring in accent so the eye has somewhere to
       land. The outer rings run past the frame on purpose — a cut through
       something larger, not an object floating in a box.

       The motion is a sixty-second weight cycle in CSS, slow enough to be felt
       rather than watched. Each ring sits at its own scattered phase, so the
       drawing never resolves into a sweep. Depth comes from the weight rather than
       from a fixed opacity ramp, so every ring holds the same opacity and the
       animation carries the hierarchy. See .dl-hero-figure in home.css. */
    const getHeroFigure = () => {

        return (
            <figure aria-hidden={'true'} className={'dl-hero-figure'}>
                <svg fill={'none'} viewBox={'40 40 560 370'} xmlns={'http://www.w3.org/2000/svg'}>
                    {RINGS.map(ring => (
                        <path
                        d={ring.d}
                        key={ring.ring}
                        className={ring.ring === ACCENT_RING ? 'is-accent' : ''}
                        stroke={'var(--sketch)'}
                        strokeOpacity={ring.ring === ACCENT_RING ? 0.9 : 0.4}
                        /* Scattered phase, not an ordered one. Stepping the delay
                           by ring index swept the swell through the drawing in a
                           clean wave, which read as a mechanism; scattered, no two
                           neighbours move together. See scatter() in rings.js. */
                        style={{ animationDelay: `${-ring.phase * 60}s` }} />
                    ))}
                </svg>
            </figure>
        )
    }

    /* The first line anybody reads, so it has to fit everybody worth reaching.

       It used to be "we fix the operational problems your software was supposed
       to solve", which is a good line about half our work and a closed door on
       the rest. It assumes two things about the reader: that something is going
       wrong, and that there is already software it is going wrong in. A founder
       building a first product has neither. Nor does an established company
       adding an offering it has never sold before. Both are people we want, and
       both were being told on sight that this page was not for them.

       What replaces it leads on the diagnosis rather than the complaint, which
       is the actual difference between us and a shop that takes an order. The
       range then lives in the line underneath, where "one costly process fixed"
       and "something that doesn't exist yet" sit side by side as equals. */
    /* The tool, running.
     *
     * A header that says what it is and how far through it is, then seven
     * questions each answered by a pointer that travels and clicks, then the
     * read those answers produce and the way in.
     *
     * Every frame is rendered at once and stacked in a single grid cell, so the
     * panel is always as tall as the tallest frame and never resizes while it
     * plays. That matters beyond the panel: it used to grow and shrink with
     * each question, which shifted everything below it on the page.
     *
     * With prefers-reduced-motion nothing plays: a question with its answer and
     * the read sit together as one still frame, and the pointer never appears.
     */
    /* The two objects are different kinds of artifact, because the two products
     * are. They were the same skeleton for a while, a bordered panel with a mono
     * header bar, one of them coloured dark, and a panel echoes another panel
     * through its shape long before its colour. design/deep-look-page.md has the
     * same lesson from that page.
     *
     * The Quick Look hands an owner a few plain sentences about their own
     * business, so it is set as prose on the door's own ground with no chrome
     * around it. The Deep Look hands back a ranked report a machine produced, so
     * it is set as one: numbered, mono-labelled, inside a document.
     *
     * Every string in the read is the real model's output for one owner's
     * answers, from files/quick-look-model.js. */
    const getQuickLookRead = () => {
        return (
            <div className={'hp-read'}>
                <p className={'hp-read-lead'}>Here’s what came back for one owner.</p>
                <ul className={'hp-read-list'}>
                    {DEMO_READ.findings.map(finding => (
                        <li key={finding.key}>
                            <b>{finding.title}</b>
                            <span>{finding.detail}</span>
                        </li>
                    ))}
                </ul>
            </div>
        )
    }

    /* Illustrative, and labelled as such. Deliberately not the conversation from
       /deep-look's hero, which made the two pages look like one page, and not one
       of the four real findings, which come from a futures trading platform and
       describe a product nobody reading this page owns. */
    const getDeepLookFinding = () => {
        return (
            <div className={'hp-find'}>
                <div className={'hp-find-bar'}>
                    <span>deep-look · report</span>
                    <span>an example</span>
                </div>
                <div className={'hp-find-row'}>
                    <span className={'hp-find-no'}>01</span>
                    <div className={'hp-find-body'}>
                        <b>Deletion doesn’t do what your policy says</b>
                        <span>Their uploads, their order history and the copies in your reporting all stay behind.</span>
                        <span className={'hp-find-do'}>Keep the record, strip what identifies them. Your reporting still adds up.</span>
                    </div>
                </div>
                <p className={'hp-find-foot'}>
                    <span>04 of 08 areas opened</span>
                    <span>ranked worst first</span>
                </p>
            </div>
        )
    }

    const getHero = () => {
        return (
            <section className={'hp-hero'} id={'top'}>
                {getEdge('left', FOLD_LEFT, 'hp-fold-edge', FOLD_HEIGHT, 0.17)}
                {getEdge('right', FOLD_RIGHT, 'hp-fold-edge', FOLD_HEIGHT, 0.17)}
                <div className={'dl-wrap hp-fold-inner'}>
                    <header className={'hp-fold-say'}>
                        <h1 className={'dl-display'}>
                            Software your business <em>actually</em> needs.
                        </h1>
                        <p className={'hp-hero-sub'}>
                            We start by working out what’s actually wrong and what it’s costing you.
                            Then we build the software that fixes it, around the systems you
                            already run.
                        </p>
                    </header>

                    {/* Two doors, below the sentence that says who we are rather than
                        instead of it. An earlier version put them in the first screen
                        with nothing above them, and a reader had to work out there were
                        two products before the page had said what the company does.

                        They are deliberately not a matched pair. Different grounds,
                        different objects, and the situation each one answers is the
                        first thing read in both. The Quick Look takes the greater width
                        because it is free and it is the front door; the Deep Look is
                        narrower and darker, and it is still a door. */}
                    <div className={'hp-doors'}>
                        <div className={'hp-door'}>
                            <span className={'hp-door-tag'}>How your business runs</span>
                            <h2 className={'hp-door-head'}>See where you’re losing time and money.</h2>
                            {getQuickLookRead()}
                            <div className={'hp-door-act'}>
                                <a className={'dl-btn dl-btn-lg'} href={locationForRoute({ view: 'quick-look' })} onClick={onQuickLookClick}>
                                    Take a Quick Look <span className={'dl-arrow'}>→</span>
                                </a>
                                <span className={'hp-door-note'}>Seven questions. Free, about three minutes.</span>
                            </div>
                        </div>

                        <div className={'hp-door is-deep'}>
                            <span className={'hp-door-tag'}>Software you already have</span>
                            <h2 className={'hp-door-head'}>Find out what’s wrong and what to fix first.</h2>
                            {getDeepLookFinding()}
                            <div className={'hp-door-act'}>
                                <a className={'dl-btn'} href={locationForRoute({ view: 'deep-look' })} onClick={onDeepLookClick}>
                                    Start a Deep Look <span className={'dl-arrow'}>→</span>
                                </a>
                                <span className={'hp-door-note'}>An engineer’s review, run by ChatGPT, Claude or Grok.</span>
                            </div>
                        </div>
                    </div>

                    {/* The fold had neither of these. Both objects are assessments, so a
                        skimmer read two tools and concluded this is a diagnostics company.

                        The line has to answer "what are these, then", and it has to do it
                        without saying the two doors do the same job. A version that said
                        "both of them do the same job" argued the paid one out of existence:
                        if the free one does the same work, there is no reason to buy the
                        other. What they share is the posture, not the job, and the two
                        labels above already say what each one looks at.

                        It also used to end on the software still running years later. That
                        claim belongs to the proof section, which is built around it, and
                        repeating it here spent it early on a reader with no reason yet to
                        believe it. */}
                    <p className={'hp-doors-note'}>
                        Each one is the first look we’d take at the start of an engagement. You can
                        have it now, before you decide whether to build anything at all.
                    </p>
                </div>
            </section>
        )
    }

    const getAlternative = () => {

        const evidence = [{
            figure: '45%',
            claim: 'of AI-generated code samples introduced an OWASP Top 10 vulnerability, across an 80-task security benchmark and more than 100 models.',
            source: 'Veracode, 2025 GenAI Code Security Report',
            href: 'https://www.veracode.com/resources/analyst-reports/2025-genai-code-security-report/'
        },{
            figure: '4×',
            claim: 'more code cloning in 2024, and refactored code has fallen from a quarter of all changes in 2021 to under a tenth.',
            source: 'GitClear, AI Copilot Code Quality 2025',
            href: 'https://www.gitclear.com/ai_assistant_code_quality_2025_research'
        },{
            figure: '7.2%',
            claim: 'drop in delivery stability for every 25% increase in AI adoption. Throughput fell as well, so it wasn’t shipping faster either.',
            source: 'Google Cloud DORA, 2024 State of DevOps Report',
            href: 'https://dora.dev/research/2024/dora-report/'
        }];
        return (
            <section className={'hp-evidence'}>
                <div className={'dl-wrap'}>
                    <header className={'hp-evidence-head'}>
                        <h2 className={'dl-display'}>
                            Generating the software is the cheap part.
                        </h2>
                        <p>
                            Anyone can have an app generated in a couple of days. The cost turns up
                            later, when it meets real customers and nobody who built it understands
                            it well enough to fix it.
                        </p>
                    </header>

                    <div className={'hp-evidence-grid'}>
                        {evidence.map((item, index) => (
                            <article className={'hp-stat'} key={index}>
                                <span className={'hp-stat-figure'}>{item.figure}</span>
                                <p>{item.claim}</p>
                                <span className={'hp-stat-source'}>
                                    <a href={item.href} target={'_blank'} rel={'noopener noreferrer'}>{item.source}</a>
                                </span>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    /* The Deep Look, on the home page. A signpost, not a second offer: it says
       who it is for, shows it working once, and hands off to /deep-look, which
       is the page that sells it. Why it sits here, why it is dark, and why it
       does not try to close: design/home-page.md. */
    const getDeepLook = () => {

        /* Short versions of the eight areas /deep-look lists in full. Named, so
           an owner can see their own worry in the list, and clipped to one line
           so the grid stays a list rather than becoming eight paragraphs. */
        const areas = [
            ['Viability and fit', 'Whether the plan still holds up when somebody asks about it.'],
            ['Security', 'Who can reach what, against how a well-built product gates it.'],
            ['Scale and infrastructure', 'Growing means adding capacity, or it means a rewrite.'],
            ['Payments', 'Whether your own records and your payment processor agree.'],
            ['Data handling', 'Whether the code can actually do what your privacy policy promises.'],
            ['Third-party services', 'What it would take to leave a vendor if its terms changed.'],
            ['Operations', 'What a customer sees when something breaks, and whether you’re told.'],
            ['Maintainability', 'Whether somebody could change it later without breaking something else.']
        ];

        return (
            <section className={'hp-deep'} id={'deep-look'}>
                <div className={'dl-wrap'}>
                    <header className={'hp-deep-head'}>
                        <div>
                            <span className={'dl-eyebrow'}>The Deep Look</span>
                            <h2 className={'dl-display'}>Already had software built? Get a second opinion.</h2>
                        </div>
                        <p className={'hp-deep-lede'}>
                            Whoever built it is usually the only person who can tell you whether it’s
                            any good, and they aren’t a neutral party. The Deep Look is the discovery
                            work we do at the start of every engagement, written down so ChatGPT,
                            Claude or Grok can run it over the software you already have. It writes up what
                            it finds in plain language, so the report stands on its own for somebody
                            who has never seen this page.
                        </p>
                    </header>

                    <ol className={'hp-deep-areas'}>
                        {areas.map(([name, line], index) => (
                            <li key={name}>
                                <span className={'hp-deep-no'}>{String(index + 1).padStart(2, '0')}</span>
                                <b>{name}</b>
                                <span className={'hp-deep-line'}>{line}</span>
                            </li>
                        ))}
                    </ol>

                    {/* Before the buttons, not after them: a cold reader got as far as
                        clicking without knowing it cost anything, and "thirty days" on
                        its own read as a subscription nobody had explained. */}
                    <div className={'hp-deep-foot'}>
                        <p className={'hp-deep-terms'}>
                            <b>$199, paid once.</b> That gives you thirty days to run it as often as
                            you like, so you can fix what it finds and run it again to check the fixes
                            held. After thirty days it stops answering. Nothing renews, and your code
                            never reaches us.
                        </p>
                        <div className={'hp-deep-act'}>
                            <a className={'dl-btn'} href={locationForRoute({ view: 'deep-look' })} onClick={onDeepLookClick}>
                                Start a Deep Look <span className={'dl-arrow'}>→</span>
                            </a>
                            <a className={'dl-btn dl-btn-outline'} href={locationForRoute({ view: 'deep-look-report' })} onClick={onReportClick}>
                                Read a real report <span className={'dl-arrow'}>→</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    // PLACEHOLDER — not for launch. Two diagram concepts were rejected here (an
    // integration map and a scope ladder); both illustrated the adjacent sentence
    // instead of adding to it. Replace with artwork that carries its own idea, or
    // delete this and let the section run on warm space.
    /* Five forms, each holding the next, nested hard off-centre so the sizes are
       obviously different and the whole cluster reads as having grown out of the
       smallest one. That is the section's argument without a word of it: the work
       is sized to the problem, and the big relationships started as a small fix.

       Same hand as the hero — generated from the same wobble, line only, no fill,
       no labels, one accent — and the same slow weight cycle, so the two drawings
       read as one vocabulary rather than as a still and a moving one. */
    const getScopeFigure = () => {

        const forms = scopeForms();
        const smallest = 0;

        return (
            <figure aria-hidden={'true'} className={'dl-land-figure'}>
                <svg fill={'none'} viewBox={'10 -20 600 500'} xmlns={'http://www.w3.org/2000/svg'}>
                    {forms.map(form => (
                        <path
                        className={form.form === smallest ? 'is-accent' : ''}
                        d={form.d}
                        key={form.form}
                        stroke={'var(--sketch)'}
                        strokeOpacity={form.form === smallest ? 0.95 : 0.42}
                        /* Same weight cycle as the hero, and scattered the same
                           way rather than stepped from the middle outward. */
                        style={{ animationDelay: `${-form.phase * 60}s` }} />
                    ))}
                </svg>
            </figure>
        )
    }

    // How we work: scope is the argument. Sage band so it reads as its own moment
    // between the ink panel and the problem list. Do not narrow this back down to
    // "small and cheap" — the range is the point.
    const getHowWeWork = () => {
        return (
            <section className={'dl-land'} id={'how-we-work'}>
                <div className={'dl-wrap'}>
                    <div className={'dl-land-top'}>
                        <div>
                            <h2 className={'dl-display'}>
                                We size the work to the problem in front of us.
                            </h2>
                            <p className={'dl-lede dl-land-intro'}>
                                Sometimes the right move is one workflow fixed, alongside the systems you’re keeping. Other times it’s the platform underneath, rebuilt properly. What decides it is what the problem is costing you, not what we’d rather sell. Most of our longest relationships started as one small fix, the kind of job that looks too minor to bother a developer with, and then kept growing for years. That’s how most of them have gone.
                            </p>
                            <a className={'dl-read dl-land-more'} href={locationForRoute({ view: 'how-we-work' })} onClick={onProcessClick}>
                                The whole process, step by step <span className={'dl-arrow'}>→</span>
                            </a>
                        </div>
                        {getScopeFigure()}
                    </div>

                    <div className={'dl-land-cols'}>
                        <div className={'dl-land-col'}>
                            <h3>Scoped to the problem.</h3>
                            <p>We start from what’s actually broken and what it’s costing you. Everything else follows from that.</p>
                        </div>
                        <div className={'dl-land-col'}>
                            <h3>Engineered into your operation.</h3>
                            <p>Whatever you already run that works, we build around rather than replace for the sake of it. Anything that’s genuinely holding you back is a different conversation.</p>
                        </div>
                        <div className={'dl-land-col'}>
                            <h3>Built to carry more later.</h3>
                            <p>Each piece stands on its own and is built to carry the next one, so nothing has to be torn up when the scope grows. You expand when you’re ready.</p>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    /* The owner's own complaint is the headline; the answer sits under it at
       reading width. Nothing is set in columns and nothing is hidden behind a
       click, because the whole job of this section is recognition — an owner
       scans the large lines, finds the one that is theirs, and reads exactly
       one answer.

       `article` is optional and must name a piece the server publishes. An
       unknown slug renders nothing rather than a dead link, so leaving one in
       after a piece is unpublished costs a link, not a broken page.

       The list has to hold a range: a franchise operator and a founder with one
       broken workflow should both find themselves in it. */
    const getProblems = () => {

        const problems = [{
            quote: 'The same information gets entered in five different places.',
            answer: 'Your systems don’t talk, so it gets retyped all day. We bridge the gap, so it’s entered once and lands everywhere it belongs.',
            article: 'manual-re-entry'
        },{
            quote: 'Every location does it differently, and we can’t see any of it.',
            answer: 'Each site solved it locally, and nobody ever went back and picked one way. We standardise the process and roll the numbers up, so head office sees every location at once.'
        },{
            quote: 'Our vendor’s software can’t do what we need, and they won’t change it.',
            answer: 'Their priorities are set by their whole customer base, not by you. Usually we build the missing capability around what you already have, and replace their software outright when that isn’t enough.',
            article: 'when-custom-software-is-worth-it'
        },{
            quote: 'It works fine until the busy season, then everything falls over.',
            answer: 'It was sized for the volume you had when it was built, and nobody went back and sized it for the peak. We build for the load you’re heading toward, and test it there before the season arrives.'
        },{
            quote: 'Only one person knows how this critical process works.',
            answer: 'If that person is off sick or leaves, the process stops with them. We turn the dependency into a system, and build the handover into it.',
            article: 'single-point-of-failure'
        },{
            quote: 'We can’t get a straight answer on how we’re really doing.',
            answer: 'The numbers live in three systems that don’t agree, so somebody rebuilds the picture by hand every month. We put the reporting where the work happens, so the answer is there when you ask for it.'
        }];

        return (
            <section className={'hp-problems'} id={'problems'}>
                <div className={'dl-wrap'}>
                    <header className={'hp-head'}>
                        <h2 className={'dl-display'}>The problems we’re usually brought in to fix.</h2>
                        <p>
                            These come up again and again, in businesses with nothing else in common.
                            A franchise operator and a hardware manufacturer will describe the same
                            problem in nearly the same words.
                        </p>
                    </header>

                    <div className={'hp-problem-grid'}>
                        {problems.map((problem, index) => (
                            <article className={'hp-problem'} key={index}>
                                <blockquote>“{problem.quote}”</blockquote>
                                <p>{problem.answer}</p>
                                {getArticleLink(problem.article)}
                            </article>
                        ))}
                    </div>

                    <div className={'hp-problems-more'}>
                        <span>Yours isn’t on this list? Describe it in a sentence.</span>
                        <a className={'dl-btn'} href={locationForRoute({ view: 'contact' })} onClick={onContactClick}>
                            Tell us what you’re trying to do <span className={'dl-arrow'}>→</span>
                        </a>
                    </div>
                </div>
            </section>
        )
    }

    /* Proof without client names. Tenure is the credibility here, so it belongs
       inside the sentence — a status pill under each entry said the same thing
       twice and read as decoration.

       Each entry instead ends with the piece of writing that goes deeper on what
       it demonstrates. That gives an interested reader somewhere to go and shows
       the experience rather than asserting it. */
    const getProof = () => {

        const systems = [{
            claim: 'Infrastructure that hasn’t stopped.',
            detail: 'We architected, and still operate, the real-time infrastructure behind roughly 1 million smart-home safety sensors worldwide, sustaining 10,000+ requests a minute around the clock.',
            article: 'deliberateness'
        },{
            claim: 'A sales operation across three countries.',
            detail: 'A custom lead-generation and sales CRM has run the marketing and sales lifecycle for franchise operators across the US, Canada and the Philippines for more than five years, with subscription billing and payments handled on Stripe.',
            article: 'when-custom-software-is-worth-it'
        },{
            claim: 'AI in a place where mistakes are expensive.',
            detail: 'We worked the strategy through with the Ross School of Business at the University of Michigan. What came out of that is running today: a retrieval pipeline that guides customers through complex setup inside a platform holding sensitive personal and financial records. Answers come from each customer’s own documents rather than a general model.',
            article: 'what-ai-agents-actually-are'
        },{
            claim: 'One fix that became the whole operation.',
            detail: 'One engagement began as a single manual data-entry process that field operators needed digitised. It grew into the full set of systems that business now runs on. It’s still growing, and it’s still protecting families today.',
            article: 'manual-re-entry'
        }];

        return (
            <section className={'dl-proof'}>
                <div className={'dl-wrap'}>
                    <div className={'dl-proof-head'}>
                        <h2 className={'dl-display'}>Systems we built years ago, still running today.</h2>
                        <p>
                            We keep client details private, so judge us on the pattern instead. We
                            build software businesses depend on, and years later we’re usually
                            still the ones maintaining it.
                        </p>
                    </div>
                    <div className={'dl-proof-cols'}>
                        {systems.map((system, index) => (
                            <div className={'dl-proof-col'} key={index}>
                                <h3>{system.claim}</h3>
                                <p>{system.detail}</p>
                                {getArticleLink(system.article)}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    // A held statement on the left, the three claims listed against it on the
    // right. Each claim is a heading and a sentence — nothing runs in, nothing
    // steps sideways.
    const getWhy = () => {

        const claims = [{
            title: 'Senior engineers only.',
            text: 'No juniors learning on your project. Whoever scopes your system is who builds it, and they have been shipping and maintaining production software for over a decade.'
        },{
            title: 'The team that builds it stays with it.',
            text: 'Architecture, integrations, infrastructure and support after launch all sit with the same people. Nothing gets handed off to a maintenance desk that has never seen the code.'
        },{
            title: 'Continuity is part of the delivery.',
            text: 'Documentation, access and handover are built into the work, so your business is never one person away from a problem. That includes being one person away from us.'
        },{
            title: 'We engineer around what you already run.',
            text: 'We fit into the systems you’re keeping and scope against the problem in front of you. We expand when the work earns it, not before.'
        }];

        return (
            <section className={'dl-why'} id={'why'}>
                <div className={'dl-wrap dl-why-grid'}>
                    <div>
                        <h2 className={'dl-display'}>You don’t have to take any of this on faith.</h2>
                        <p className={'dl-why-sub'}>
                            Some of what we built years ago is still in production, and we still
                            maintain it. That’s what we think finished is supposed to look like.
                        </p>
                        <div className={'dl-why-check'}>
                            <p>Ask us what we built longest ago that’s still running, and ask to speak to the people running it.</p>
                            <a className={'dl-quiet'} href={'#contact'} onClick={onContactClick}>
                                Ask for references
                            </a>
                        </div>
                    </div>
                    <div>
                        {claims.map((claim, index) => (
                            <div className={'dl-why-item'} key={index}>
                                <h3>{claim.title}</h3>
                                <p>{claim.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    // Second dark ground. The section makes its own claim first — quotes support
    // an argument here, they do not stand in for one. Four voices, equal weight,
    // attribution above each so every quote has an entry point.
    const getTestimonials = () => {

        const quotes = [{
            name: 'R. Robinson IV',
            role: 'Founder & CEO, eCarra',
            quote: 'Duralabs is an exceptional full-stack development team. Incredibly detailed, fast, and efficient — and they excel at building scalable platforms that can grow with your business.'
        },{
            name: 'J. Carriere',
            role: 'Founder & CEO, Silicon Union',
            quote: 'For custom platform development, this is the only place you need to look. Consistently top quality work with innovative features.'
        },{
            name: 'A. Graham',
            role: 'Founder & CEO, FareRyder',
            quote: 'They deliver on their promise to help companies grow faster. Their approach goes beyond just coding.'
        },{
            name: 'R. Worthey',
            role: 'Founder, PressOnPrez',
            quote: 'It required someone with his expertise to get it off the ground. Great about offering insights to make the product better.'
        }];

        return (
            <section className={'dl-quotes'}>
                <div className={'dl-wrap'}>
                    <h2 className={'dl-display'}>These are the people we’d put you in touch with.</h2>
                    <p className={'dl-quotes-intro'}>
                        Founders who hired us and shipped with us. They’ll answer the phone if you
                        want to ask what it was like.
                    </p>

                    <div className={'dl-quotes-grid'}>
                        {quotes.map((item, index) => (
                            <div className={'dl-quote'} key={index}>
                                <div className={'dl-quote-name'}>
                                    {item.name} <span>· {item.role}</span>
                                </div>
                                <blockquote>“{item.quote}”</blockquote>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    /* Marginalia, not illustration. Contour lines packed against the outer edge
       and opening out as they move inward, running off the top and bottom of the
       band — they frame the call to action rather than compete with it. They are
       decoration, so they are hidden from assistive technology and from any
       screen too narrow to spare the room. */
    /* The height and the opacity are arguments because the two bands that use
       this are not the same shape or the same ground. The fold is about twice
       the Quick Look band's height, so its field is drawn taller rather than
       stretched, and it sits on --paper rather than on the warm band, where the
       same stroke reads far louder. */
    const getEdge = (side, field, className, height = 430, opacity = 0.3) => {
        return (
            <figure aria-hidden={'true'} className={`${className} is-${side}`}>
                <svg
                fill={'none'}
                preserveAspectRatio={side === 'left' ? 'xMinYMid slice' : 'xMaxYMid slice'}
                viewBox={`0 0 300 ${height}`}
                xmlns={'http://www.w3.org/2000/svg'}>
                    {field.map(contour => (
                        <path
                        d={contour.d}
                        key={contour.line}
                        stroke={'var(--sketch)'}
                        strokeLinecap={'round'}
                        strokeOpacity={opacity}
                        style={{ animationDelay: `${-contour.phase * 60}s` }} />
                    ))}
                </svg>
            </figure>
        )
    }

    // The only centred block on the page. That is the whole reason it lands.
    const getQuickLook = () => {
        return (
            <section className={'dl-quick-look'} id={'quick-look'}>
                {getEdge('left', EDGE_LEFT, 'dl-quick-look-edge')}
                {getEdge('right', EDGE_RIGHT, 'dl-quick-look-edge')}
                <div className={'dl-wrap'}>
                    <div className={'dl-quick-look-inner'}>
                        <div className={'dl-eyebrow'}>Free · No call required</div>
                        <h2 className={'dl-display'}>The <em>Quick</em> Look</h2>
                        <p>
                            A few questions about how your business actually runs, like where
                            information gets stuck and what depends on one person. Nothing to
                            download and nobody to call. Three minutes later you get a straight
                            read on where your risk sits, what it’s likely costing you, and which
                            piece is worth dealing with first.
                        </p>
                        <a className={'dl-btn'} href={locationForRoute({ view: 'quick-look' })} onClick={onQuickLookClick}>
                            Take a Quick Look <span className={'dl-arrow'}>→</span>
                        </a>
                        <p className={'dl-reassure'}>
                            About three minutes. No email required to see your results.
                        </p>
                    </div>
                </div>
            </section>
        )
    }

    /* The section asks for something, so it has to provide the means. Three
       fields is the whole form — an owner who has read this far is willing to
       type a sentence, not fill in a project brief. The rest of what the old
       contact form collected (services, budget, timeline) belongs in the reply,
       not in the way of it.

       Posts to the same endpoint the legacy contact form uses, so submissions
       land where they always have. Failure is reported honestly and the direct
       details stay on screen either way, for anyone who would rather not use a
       form at all. */
    const getClosing = () => {

        const disabled = sending || !enquiry.name || !enquiry.email || !enquiry.problem;

        return (
            <section className={'dl-closing'} id={'contact'}>
                <div className={'dl-wrap dl-closing-grid'}>
                    <div className={'dl-closing-say'}>
                        <h2 className={'dl-display'}>
                            Tell us what you’re trying to do. We’ll tell you honestly whether we
                            can help.
                        </h2>
                        <p>
                            A process that isn’t working, or a piece of software you’ve wanted to
                            build and never got to. One sentence is plenty. If software turns out
                            not to be the right answer here, we’ll say so plainly rather than talk
                            you into a project that was never going to pay for itself.
                        </p>
                    </div>

                    {sent ? (
                        <div className={'dl-form dl-form-done'}>
                            <h3>That’s with us.</h3>
                            <p>
                                We read every one of these ourselves and reply within a business
                                day. If it’s urgent, call us. The number is on the left.
                            </p>
                        </div>
                    ) : (
                        <form className={'dl-form'} onSubmit={onEnquirySubmit}>
                            <BotTrap />
                            <label className={'dl-field'}>
                                <span>Your name</span>
                                <input
                                onChange={onEnquiryChange('name')}
                                type={'text'}
                                value={enquiry.name} />
                            </label>
                            <label className={'dl-field'}>
                                <span>Email</span>
                                <input
                                onChange={onEnquiryChange('email')}
                                type={'email'}
                                value={enquiry.email} />
                            </label>
                            <label className={'dl-field'}>
                                <span>What are you trying to do?</span>
                                <textarea
                                onChange={onEnquiryChange('problem')}
                                placeholder={'We want to offer our customers online booking and nothing we have will do it.'}
                                rows={4}
                                value={enquiry.problem} />
                            </label>
                            {failure && <p className={'dl-form-error'}>{failure}</p>}
                            <button className={'dl-btn'} disabled={disabled} type={'submit'}>
                                {sending ? 'Sending…' : 'Send it over'}
                                {!sending && <span className={'dl-arrow'}>→</span>}
                            </button>
                        </form>
                    )}
                </div>
            </section>
        )
    }

    return (
        <div className={'dl'}>
            <SiteNav utils={utils} variant={'home'} />
            {getHero()}
            {getAlternative()}
            {getHowWeWork()}
            {getProblems()}
            {getProof()}
            {getDeepLook()}
            {getWhy()}
            {getTestimonials()}
            {getQuickLook()}
            {getClosing()}
            <SiteFooter utils={utils} variant={'home'} />
        </div>
    )
}

export default HomePage;
